import React, { useState, useEffect, useMemo } from 'react';
import { pricing as localPricing } from '../../data/pricing';
import { proFetch, estPatron } from '../../utils/proApi';
import { Page, EnTetePage, Icone } from '../../components/pro/ui';

const CAT_LABELS = {
  sols: 'Sols & revêtements', murs: 'Murs & plafonds', preparation: 'Préparation supports',
  plomberie: 'Plomberie & sanitaires', electricite: 'Électricité & éclairage',
  menuiseries: 'Menuiseries & rangements', isolation: 'Isolation',
  chauffage: 'Chauffage & climatisation', demolition: 'Démolition & gros œuvre',
  divers: 'Divers & chantier', ite: 'Isolation thermique ext.', facade: 'Façade & ravalement',
};

function fmt(n) {
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n);
}

export default function ProReferentiel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newItem, setNewItem] = useState({ category: 'sols', id: '', label: '', unit: 'm²', priceMin: 0, priceMax: 0 });

  // Avis donnés sur la grille par un lien public (pro/grille.feedback.js) : lus,
  // jamais appliqués d'eux-mêmes. Direction seulement.
  const [avis, setAvis] = useState({});       // cle → [{ par, delta, prix, commentaire }]
  const [liens, setLiens] = useState([]);
  const [lienNom, setLienNom] = useState('');
  const [lienCree, setLienCree] = useState(null);
  const patron = estPatron();
  useEffect(() => {
    if (!patron) return;
    proFetch('/pricing/grille/avis').then(r => r.json()).then(list => {
      const m = {};
      for (const a of list || []) (m[a.cle] ||= []).push(a);
      setAvis(m);
    }).catch(() => {});
    proFetch('/pricing/grille/liens').then(r => r.json()).then(l => setLiens(Array.isArray(l) ? l : [])).catch(() => {});
  }, [patron]);
  const creerLien = async () => {
    const nom = lienNom.trim();
    if (!nom) return;
    const r = await proFetch('/pricing/grille/liens', { method: 'POST', body: JSON.stringify({ nom }) });
    const l = await r.json();
    if (!r.ok) return;
    setLienCree(`${window.location.origin}${l.url}`);
    setLiens(prev => [{ ...l, actif: true, avis: 0, createdAt: new Date().toISOString() }, ...prev]);
    setLienNom('');
  };

  // Fetch from DB, fallback to local pricing.js
  useEffect(() => {
    (async () => {
      try {
        const res = await proFetch('/pricing');
        const data = await res.json();
        if (data && Object.keys(data).length > 0) {
          const flat = [];
          for (const [cat, entries] of Object.entries(data)) {
            for (const [id, item] of Object.entries(entries)) {
              flat.push({ category: cat, id, ...item });
            }
          }
          setItems(flat);
        } else {
          // Seed from local
          await seedFromLocal();
        }
      } catch {
        loadLocal();
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLocal = () => {
    const flat = [];
    for (const [cat, entries] of Object.entries(localPricing)) {
      for (const [id, item] of Object.entries(entries)) {
        flat.push({ category: cat, id, ...item });
      }
    }
    setItems(flat);
  };

  const seedFromLocal = async () => {
    const seedItems = [];
    for (const [cat, entries] of Object.entries(localPricing)) {
      for (const [id, item] of Object.entries(entries)) {
        seedItems.push({ category: cat, id, label: item.label, unit: item.unit, priceMin: item.priceMin, priceMax: item.priceMax });
      }
    }
    try {
      await proFetch('/pricing/seed', {
        method: 'POST',
        body: JSON.stringify({ items: seedItems }),
      });
      setItems(seedItems.map(i => ({ ...i, price: Math.round((i.priceMin + i.priceMax) / 2) })));
    } catch {
      loadLocal();
    }
  };

  // Filter
  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(i => i.label.toLowerCase().includes(q) || i.category.includes(q) || i.id.includes(q));
  }, [items, search]);

  // Group by category
  const grouped = useMemo(() => {
    const g = {};
    for (const item of filtered) {
      if (!g[item.category]) g[item.category] = [];
      g[item.category].push(item);
    }
    return g;
  }, [filtered]);

  const startEdit = (item) => {
    const key = `${item.category}.${item.id}`;
    setEditingKey(key);
    setEditValues({ label: item.label, unit: item.unit, priceMin: item.priceMin, priceMax: item.priceMax });
  };

  const saveEdit = async (item) => {
    try {
      await proFetch(`/pricing/${item.category}/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify(editValues),
      });
      setItems(prev => prev.map(i =>
        i.category === item.category && i.id === item.id
          ? { ...i, ...editValues, price: Math.round((editValues.priceMin + editValues.priceMax) / 2) }
          : i
      ));
      setEditingKey(null);
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Supprimer "${item.label}" ?`)) return;
    try {
      await proFetch(`/pricing/${item.category}/${item.id}`, { method: 'DELETE' });
      setItems(prev => prev.filter(i => !(i.category === item.category && i.id === item.id)));
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const addItem = async () => {
    if (!newItem.id || !newItem.label) return;
    try {
      await proFetch('/pricing/item', {
        method: 'POST',
        body: JSON.stringify(newItem),
      });
      setItems(prev => [...prev, { ...newItem, price: Math.round((newItem.priceMin + newItem.priceMax) / 2) }]);
      setNewItem({ category: 'sols', id: '', label: '', unit: 'm²', priceMin: 0, priceMax: 0 });
      setShowAdd(false);
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  if (loading) return <div className="text-center py-20 text-slate-400">Chargement du référentiel...</div>;

  return (
    <Page>
      <EnTetePage titre="Référentiel de prix" sous={`${items.length} postes — ${Object.keys(grouped).length} catégories`}>
        <>
          <button onClick={() => setShowAdd(!showAdd)} className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-4 py-2.5 rounded-xl text-sm">
            + Nouveau poste
          </button>
          {items.length === 0 && (
            <button onClick={seedFromLocal} className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold px-4 py-2.5 rounded-xl text-sm">
              Charger les prix de départ
            </button>
          )}
        </>
      </EnTetePage>

      <input
        type="search"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Rechercher (carrelage, peinture, plomberie...)"
        className="w-full max-w-lg px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:border-orange-500 outline-none mb-5"
      />

      {/* Add new item form */}
      {patron && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3 mb-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-800">Avis sur la grille</span>
            <span className="text-xs text-slate-500">un lien par personne, à envoyer sur son téléphone ; ses + / − s'affichent ici à côté de chaque poste, rien ne s'applique tout seul.</span>
            <div className="ml-auto flex items-center gap-2">
              <input value={lienNom} onChange={e => setLienNom(e.target.value)} placeholder="Prénom (ex. Sorin)" className="champ w-40" />
              <button onClick={creerLien} disabled={!lienNom.trim()} className="px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold disabled:opacity-40">Créer un lien</button>
            </div>
          </div>
          {lienCree && <p className="mt-2 text-xs text-emerald-700 break-all">Lien créé : <a href={lienCree} target="_blank" rel="noreferrer" className="underline">{lienCree}</a></p>}
          {liens.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
              {liens.map(l => <li key={l.id}><a href={l.url} target="_blank" rel="noreferrer" className="font-semibold text-slate-700 underline">{l.nom}</a> · {l.avis} avis · {new Date(l.createdAt).toLocaleDateString('fr-FR')}</li>)}
            </ul>
          )}
        </div>
      )}
      {showAdd && (
        <div className="bg-orange-50 rounded-xl p-5 border border-orange-200 mb-5">
          <h3 className="font-bold text-sm text-orange-900 mb-3">Nouveau poste</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <select value={newItem.category} onChange={e => setNewItem(p => ({ ...p, category: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm bg-white">
              {Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input placeholder="ID (ex: ragréage)" value={newItem.id} onChange={e => setNewItem(p => ({ ...p, id: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder="Libellé" value={newItem.label} onChange={e => setNewItem(p => ({ ...p, label: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm" />
            <select value={newItem.unit} onChange={e => setNewItem(p => ({ ...p, unit: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm bg-white">
              {['m²', 'ml', 'u', 'forfait', 'logement', 'niveau', 'kW', 'semaine'].map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            <div className="flex gap-2">
              <input type="number" placeholder="Min" value={newItem.priceMin || ''} onChange={e => setNewItem(p => ({ ...p, priceMin: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-2 border rounded-lg text-sm text-center" />
              <input type="number" placeholder="Max" value={newItem.priceMax || ''} onChange={e => setNewItem(p => ({ ...p, priceMax: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-2 border rounded-lg text-sm text-center" />
            </div>
            <button onClick={addItem} className="bg-orange-600 text-white font-bold rounded-lg text-sm">Ajouter</button>
          </div>
        </div>
      )}

      {/* Items by category */}
      {Object.entries(grouped).map(([cat, catItems]) => (
        <div key={cat} className="mb-6">
          <h2 className="font-bold text-sm text-slate-500 uppercase tracking-wider mb-2 px-1">
            {CAT_LABELS[cat] || cat} ({catItems.length})
          </h2>
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs text-slate-400">
                  <th className="text-left px-4 py-2.5 font-semibold">Libellé</th>
                  <th className="text-left px-3 py-2.5 font-semibold w-16">Unité</th>
                  <th className="text-right px-3 py-2.5 font-semibold w-24">Min</th>
                  <th className="text-right px-3 py-2.5 font-semibold w-24">Max</th>
                  <th className="text-right px-3 py-2.5 font-semibold w-24">Moyen</th>
                  <th className="text-center px-3 py-2.5 font-semibold w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {catItems.map(item => {
                  const key = `${item.category}.${item.id}`;
                  const isEditing = editingKey === key;
                  return (
                    <tr key={key} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-2.5">
                        {isEditing ? (
                          <input value={editValues.label} onChange={e => setEditValues(p => ({ ...p, label: e.target.value }))} className="w-full px-2 py-1 border rounded text-sm" />
                        ) : (<>
                          <span className="font-medium text-slate-800">{item.label}</span>
                          {(avis[key] || []).map((a, i) => (
                            <span key={i} title={`${a.par} : ${a.prixBase} € → ${a.prix} €${a.commentaire ? `\n${a.commentaire}` : ''}`}
                                  className={`inline-flex items-center gap-1 ml-2 align-middle rounded-full px-1.5 py-0.5 text-[10px] font-semibold border ${a.delta > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : a.delta < 0 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                              {a.par} {a.delta > 0 ? '+' : ''}{Math.round(a.delta * 5)} %{a.delta ? ` → ${fmt(a.prix)} €` : ''}{a.commentaire ? ' ✎' : ''}
                            </span>
                          ))}
                          {item.origine === 'web' && (
                            <span title={`Repère trouvé en ligne, pas encore validé.${(item.sources || []).length ? '\n' + item.sources.join('\n') : ''}`}
                                  className="inline-flex items-center gap-1 ml-2 align-middle rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5 text-[10px] font-semibold">
                              <Icone nom="web" className="w-3 h-3" /> web
                            </span>
                          )}
                        </>)}
                      </td>
                      <td className="px-3 py-2.5 text-slate-500">
                        {isEditing ? (
                          <select value={editValues.unit} onChange={e => setEditValues(p => ({ ...p, unit: e.target.value }))} className="px-1 py-1 border rounded text-xs bg-white">
                            {['m²', 'ml', 'u', 'forfait', 'logement', 'niveau', 'kW', 'semaine'].map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        ) : item.unit}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {isEditing ? (
                          <input type="number" value={editValues.priceMin} onChange={e => setEditValues(p => ({ ...p, priceMin: parseFloat(e.target.value) || 0 }))} className="w-16 px-2 py-1 border rounded text-sm text-right" />
                        ) : <span className="text-slate-600">{fmt(item.priceMin)} €</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {isEditing ? (
                          <input type="number" value={editValues.priceMax} onChange={e => setEditValues(p => ({ ...p, priceMax: parseFloat(e.target.value) || 0 }))} className="w-16 px-2 py-1 border rounded text-sm text-right" />
                        ) : <span className="text-slate-600">{fmt(item.priceMax)} €</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-orange-600 whitespace-nowrap tabular-nums">
                        {fmt(isEditing ? Math.round((editValues.priceMin + editValues.priceMax) / 2) : (item.price || Math.round((item.priceMin + item.priceMax) / 2)))} €
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {isEditing ? (
                          <div className="flex gap-1 justify-center">
                            <button onClick={() => saveEdit(item)} className="px-2 py-1 bg-green-600 text-white rounded text-xs font-bold">OK</button>
                            <button onClick={() => setEditingKey(null)} className="px-2 py-1 bg-slate-200 rounded text-xs font-bold">X</button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-center">
                            <button onClick={() => startEdit(item)} className="px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-xs font-semibold text-slate-600">Modifier</button>
                            <button onClick={() => deleteItem(item)} className="px-2 py-1 bg-red-50 hover:bg-red-100 rounded text-xs font-semibold text-red-500">Suppr</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </Page>
  );
}
